import { FormattedDate, FormattedNumber, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { breakpoints, DataTable } from '@openedx/paragon';
import { OrderHistoryResponse } from 'shared/types/cart';
import { getEnv } from '@/env';
import { PageLoader } from 'components/ui/atoms/loader';
import { ErrorAlert, OrderItem, ViewOrderDetailsHyperlink } from './components';
import { OrderDetailsLink } from './components/order-details-link';
import { messages } from './messages';

const CUTOFF_IN_DAYS = 180;
const MOBILE_PAYMENT_PROCESSOR_TO_MESSAGE_MAP = new Map([
  [
    'android_iap_edx',
    'payment.order.history.page.payment.processor.heading.android',
  ],
  ['ios_iap_edx', 'payment.order.history.page.payment.processor.heading.ios'],
]);

export const OrderHistory = ({
  isError,
  isLoading,
  orders,
}: OrderHistoryResponse) => {
  const { formatMessage } = useIntl();

  const isMobileView = useMediaQuery({ maxWidth: breakpoints.small?.maxWidth });
  const transformedData =
    isError || isLoading
      ? []
      : orders.results.map((result) => {
          const isFreeOrder =
            result.total_excl_tax === '0.0' &&
            result.payment_method === 'Unknown';

          const isWebsiteOrder = !result.mobile_order;
          const paymentProcessorKey = result.payment_processor;

          return {
            items: <OrderItem lineItems={result.lines} />,
            date_placed: <FormattedDate value={result.date_placed} />,
            total_cost: (
              <FormattedNumber
                value={+result.total_excl_tax}
                // eslint-disable-next-line react/style-prop-object
                style="currency"
                currency={result.currency}
              />
            ),
            order_number: result.number,
            receipt:
              !isFreeOrder && isWebsiteOrder ? (
                <ViewOrderDetailsHyperlink orderNumber={result.number} />
              ) : !isWebsiteOrder &&
                MOBILE_PAYMENT_PROCESSOR_TO_MESSAGE_MAP.has(
                  paymentProcessorKey,
                ) ? (
                formatMessage(
                  messages[
                    MOBILE_PAYMENT_PROCESSOR_TO_MESSAGE_MAP.get(
                      paymentProcessorKey,
                    ) as keyof typeof messages
                  ],
                )
              ) : null,
            order_details: <OrderDetailsLink orderNumber={result.number} />,
          };
        });

  const hasNoOrders = transformedData.length === 0;
  const footerMessage = (
    <i>
      {formatMessage(messages['payment.order.history.page.footer'], {
        cutoffInDays: CUTOFF_IN_DAYS,
      })}
    </i>
  );

  const renderOrdersTable = () => (
    <DataTable
      isSelectable={false}
      itemCount={transformedData.length}
      data={transformedData}
      columns={[
        {
          Header: formatMessage(
            messages['payment.order.history.table.column.items'],
          ),
          accessor: 'items',
        },
        {
          Header: formatMessage(
            messages['payment.order.history.table.column.date.placed'],
          ),
          accessor: 'date_placed',
        },
        {
          Header: formatMessage(
            messages['payment.order.history.table.column.total.cost'],
          ),
          accessor: 'total_cost',
        },
        {
          Header: formatMessage(
            messages['payment.order.history.table.column.order.number'],
          ),
          accessor: 'order_number',
        },
        {
          Header: formatMessage(
            messages['payment.order.history.table.column.receipt'],
          ),
          accessor: 'receipt',
        },
        ...(getEnv('NEXT_PUBLIC_ENABLE_DYNAMIC_CURRENCY') === 'true'
          ? [
              {
                Header: formatMessage(
                  messages['payment.order.history.table.column.order.details'],
                ),
                accessor: 'order_details',
              },
            ]
          : []),
      ]}
    >
      <DataTable.Table />
      <DataTable.TableFooter>
        <div className="flex-grow-1 text-center">{footerMessage}</div>
      </DataTable.TableFooter>
    </DataTable>
  );

  const renderMobileOrdersTable = () => (
    <div>
      {transformedData.map((item) => (
        <div className="border-bottom py-3" key={item.order_number}>
          <dl>
            <dt>
              {formatMessage(
                messages['payment.order.history.table.column.items'],
              )}
            </dt>
            <dd className="tw-mb-2">{item.items}</dd>
            <dt>
              {formatMessage(
                messages['payment.order.history.table.column.date.placed'],
              )}
            </dt>
            <dd className="tw-mb-2">{item.date_placed}</dd>
            <dt>
              {formatMessage(
                messages['payment.order.history.table.column.total.cost'],
              )}
            </dt>
            <dd className="tw-mb-2">{item.total_cost}</dd>
            <dt>
              {formatMessage(
                messages['payment.order.history.table.column.order.number'],
              )}
            </dt>
            <dd className="tw-mb-2">{item.order_number}</dd>
          </dl>
          <p className="tw-mb-4">{item.receipt}</p>
          <p className="tw-mb-4">{item.order_details}</p>
        </div>
      ))}
      <div className="border-bottom py-3">{footerMessage}</div>
    </div>
  );

  if (isLoading) {
    return (
      <PageLoader
        loaderText={formatMessage(
          messages['payment.order.history.orders.loader'],
        )}
      />
    );
  }

  return (
    <div className="container-fluid py-5">
      <ErrorAlert isVisible={isError} />
      <h1 className="mb-4 tw-text-3xl md:tw-text-3.5xl">
        {formatMessage(messages['payment.order.history.page.heading'])}
      </h1>
      {hasNoOrders ? (
        <p>
          {formatMessage(messages['payment.order.history.no.orders'], {
            cutoffInDays: CUTOFF_IN_DAYS,
          })}
        </p>
      ) : isMobileView ? (
        renderMobileOrdersTable()
      ) : (
        renderOrdersTable()
      )}
    </div>
  );
};
